// Root CLAUDE.md: "All timestamps stored UTC, displayed in East Africa Time
// (UTC+3)." mysql2 returns DATETIME columns as JS Date objects already
// parsed in the server process's local time zone assumption (UTC, per
// backend/src/db/pool.js's plain createPool with no timezone override) and
// serialized by Express as ISO 8601 UTC strings — Intl.DateTimeFormat with
// an explicit IANA zone is the correct way to render that in EAT without
// any manual offset math (which would be exactly the kind of ad hoc date
// arithmetic this codebase avoids for money and should avoid here too).

const EAT_TIME_ZONE = 'Africa/Addis_Ababa';

function formatter(lang, options) {
  return new Intl.DateTimeFormat(lang === 'am' ? 'am-ET' : 'en-ET', {
    timeZone: EAT_TIME_ZONE,
    ...options,
  });
}

export function formatDate(isoString, lang = 'en') {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  return formatter(lang, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

export function formatDateTime(isoString, lang = 'en') {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  return formatter(lang, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// Timezone bug fix (found 2026-10-25, flagged by Ermiyas as real in
// production, not hypothetical): a MySQL DATE column comes back from
// mysql2 as a JS Date built at LOCAL midnight for that calendar date — on
// this server, local means whatever the OS timezone resolves to
// (confirmed empirically: Africa/Nairobi, UTC+3 — NOT the UTC
// docs/DEPLOY.md assumed by default), not UTC midnight. When that Date is
// then JSON-serialized (res.json() -> JSON.stringify -> toISOString()),
// the wire value is a UTC instant that is actually the PREVIOUS
// calendar day's late evening — e.g. DB value '2027-03-01' arrives at the
// browser as "2027-02-28T21:00:00.000Z". Slicing the first 10 characters
// of that string (`.slice(0, 10)`) reads the UTC calendar date, which is
// wrong by one day. formatDate/formatDateTime above have never had this
// bug — converting that instant back to Africa/Addis_Ababa (also UTC+3)
// happens to exactly cancel the shift mysql2 introduced — but that
// cancellation is a coincidence of the server's actual OS timezone
// matching EAT's offset, not something to rely on by writing new slicing
// code. This helper does the same explicit EAT conversion
// formatDate/formatDateTime already do, instead of a naive slice, for
// code that needs a comparable/settable YYYY-MM-DD string (a date-range
// filter, an `<input type="date">` value) rather than a human-readable
// label. formatToParts (not a locale's default ordering) so the output
// format never depends on which locale string happens to format as
// Y-M-D.
export function toEatDateString(isoString) {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EAT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const byType = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

export default formatDate;
